import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Upload, Video, X, AlertCircle, CheckCircle, Loader2, Sparkles, Music, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 50 * 1024 * 1024; // 50MB
// Server-side ffmpeg branding pipeline takes ~30–60s for a 12–15MB clip.
// We animate the indeterminate bar over BRANDING_ETA_MS so users see steady
// progress instead of a frozen spinner.
const BRANDING_ETA_MS = 60000;

const ChallengeUpload = ({ challenge, athlete, onSubmit, onCancel, submitting }) => {
  const { getAuthHeaders } = useAuth();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [score, setScore] = useState('');
  const [verticalWarning, setVerticalWarning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | uploading | branding
  const [brandingProgress, setBrandingProgress] = useState(0);
  // Music picker state
  const [includeMusic, setIncludeMusic] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [playingTrack, setPlayingTrack] = useState(null);
  const audioPreviewRef = useRef(null);
  const brandingStartedAt = useRef(null);
  const brandingTimer = useRef(null);
  const inputRef = useRef(null);

  // Load curated soundtracks once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/challenges/audio-tracks`, {
          headers: { ...getAuthHeaders() },
        });
        if (cancelled) return;
        const list = res.data?.tracks || [];
        setTracks(list);
        if (list.length && !selectedTrack) {
          const def = list.find((t) => t.is_default) || list[0];
          setSelectedTrack(def.slug);
        }
      } catch {
        // non-fatal — upload still works without music
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stop preview audio when component unmounts.
  useEffect(() => () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current = null;
    }
  }, []);

  const togglePreview = (slug) => {
    if (playingTrack === slug) {
      audioPreviewRef.current?.pause();
      audioPreviewRef.current = null;
      setPlayingTrack(null);
      return;
    }
    // Stop previous
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current = null;
    }
    const url = `${API}/challenges/audio/${slug}`;
    const headers = getAuthHeaders();
    // We need to fetch with auth then play from blob URL — <audio> can't carry headers.
    fetch(url, { headers })
      .then((r) => r.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = new Audio(blobUrl);
        a.volume = 0.65;
        a.onended = () => { setPlayingTrack(null); URL.revokeObjectURL(blobUrl); };
        audioPreviewRef.current = a;
        a.play().catch(() => {});
        setPlayingTrack(slug);
      })
      .catch(() => {});
  };

  // Animate the branding bar from 0 → ~95% across BRANDING_ETA_MS so the user
  // sees steady motion while the server is encoding. Final 100% is set when
  // the request resolves.
  useEffect(() => {
    if (phase !== 'branding') {
      if (brandingTimer.current) clearInterval(brandingTimer.current);
      brandingTimer.current = null;
      brandingStartedAt.current = null;
      return;
    }
    brandingStartedAt.current = Date.now();
    const tick = () => {
      const elapsed = Date.now() - (brandingStartedAt.current || Date.now());
      // Asymptotic curve — fast early, slows near the end so we never hit 100% prematurely.
      const ratio = Math.min(1, elapsed / BRANDING_ETA_MS);
      const eased = 1 - Math.pow(1 - ratio, 2); // ease-out quad
      setBrandingProgress(Math.min(95, Math.round(eased * 95)));
    };
    // Kick off the first tick on the next macrotask so we never call
    // setState synchronously inside the effect body (React 19 rule).
    const initial = setTimeout(tick, 50);
    brandingTimer.current = setInterval(tick, 400);
    return () => {
      clearTimeout(initial);
      if (brandingTimer.current) clearInterval(brandingTimer.current);
    };
  }, [phase]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error('Video too large. Max 50MB.');
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const ratio = video.videoWidth / video.videoHeight;
      setVerticalWarning(ratio > 1);
      URL.revokeObjectURL(video.src);
    };
    video.src = url;
  };

  const canSubmit = !!file && score !== '' && !isNaN(parseFloat(score)) && parseFloat(score) >= 0 && !uploading && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    const scoreNum = parseFloat(score);
    setUploading(true);
    setProgress(0);
    setPhase('uploading');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('challenge_id', challenge.id);
      fd.append('score', String(scoreNum));
      fd.append('include_music', includeMusic ? 'true' : 'false');
      if (includeMusic && selectedTrack) fd.append('audio_track', selectedTrack);
      const res = await axios.post(
        `${API}/challenges/upload-video/${athlete?.athlete_id}`,
        fd,
        {
          headers: { ...getAuthHeaders() },
          onUploadProgress: (e) => {
            if (e.total) {
              const pct = Math.round((e.loaded / e.total) * 100);
              setProgress(pct);
              if (pct >= 99) setPhase('branding');
            }
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 0,
        }
      );
      const { storage_id, video_url, branded_video_url, thumbnail_url } = res.data;
      const toAbs = (u) => (u && !u.startsWith('http')) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
      onSubmit({
        videoUrl: toAbs(video_url),
        brandedVideoUrl: toAbs(branded_video_url),
        thumbnailUrl: toAbs(thumbnail_url),
        publicId: storage_id,
        score: scoreNum,
      });
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      toast.error(detail || 'Upload failed. Try again.');
      setUploading(false);
      setPhase('idle');
      setProgress(0);
      setBrandingProgress(0);
    }
  };

  const buttonLabel = (() => {
    if (submitting) return 'Submitting...';
    if (phase === 'uploading' && progress < 99) return `Uploading ${progress}%`;
    if (phase === 'branding') return 'Adding Meybra overlay...';
    return 'Upload & Submit';
  })();

  return (
    <div className="space-y-6" data-testid="challenge-upload">
      <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-primary uppercase tracking-widest flex items-center gap-1">
        ← Cancel
      </button>
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Submit Your Attempt</h1>
        <p className="text-muted-foreground text-sm">{challenge.title} · {athlete?.athlete_name}</p>
      </div>

      {/* Upload area */}
      <div className="bg-card border-2 border-dashed border-border/60 rounded-sm p-8 text-center">
        {!file && (
          <>
            <Video className="mx-auto text-muted-foreground mb-3" size={48} />
            <p className="text-sm text-muted-foreground mb-4">Upload your video (max 50MB)</p>
            <Button
              onClick={() => inputRef.current?.click()}
              className="bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold"
              data-testid="upload-pick-btn"
            >
              <Upload size={16} className="mr-2" /> Choose Video
            </Button>
            <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={onPick} data-testid="upload-file-input" />
          </>
        )}
        {file && previewUrl && (
          <div className="space-y-4">
            <video src={previewUrl} controls className="max-h-80 mx-auto rounded-sm" />
            {verticalWarning && (
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-3 text-sm text-amber-300 flex items-center gap-2 justify-center">
                <AlertCircle size={14} />
                For the best experience and sharing quality, record vertically.
              </div>
            )}
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <span>{file.name}</span>
              <span>·</span>
              <span>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
              <button
                onClick={() => { setFile(null); setPreviewUrl(null); }}
                disabled={uploading}
                className="text-red-400 hover:text-red-300 ml-2 disabled:opacity-40"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Score input */}
      {file && (
        <div className="bg-card border border-border rounded-sm p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
              Your Score ({challenge.scoring_type === 'time' ? 'seconds — lower is better' : 'count — higher is better'})
            </label>
            <Input
              type="number"
              step={challenge.scoring_type === 'time' ? '0.1' : '1'}
              min="0"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              placeholder={challenge.scoring_type === 'time' ? 'e.g. 17.2' : 'e.g. 84'}
              className="text-2xl h-14 font-heading"
              disabled={uploading}
              data-testid="score-input"
            />
          </div>

          {/* Music picker — Nike-style soundtrack on top of the branded video */}
          {tracks.length > 0 && (
            <div className="border-t border-border/40 pt-4 space-y-3" data-testid="music-picker">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Music size={16} className="text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold uppercase tracking-wider">Soundtrack</p>
                    <p className="text-[11px] text-muted-foreground leading-tight">
                      Royalty-free — safe to share anywhere
                    </p>
                  </div>
                </div>
                <Switch
                  checked={includeMusic}
                  onCheckedChange={setIncludeMusic}
                  disabled={uploading}
                  data-testid="music-toggle"
                />
              </div>

              {includeMusic && (
                <div className="space-y-2">
                  {tracks.map((t) => {
                    const isSelected = selectedTrack === t.slug;
                    const isPlaying = playingTrack === t.slug;
                    return (
                      <div
                        key={t.slug}
                        className={`flex items-start gap-3 p-3 rounded-sm border transition-colors cursor-pointer ${
                          isSelected
                            ? 'border-primary/60 bg-primary/10'
                            : 'border-border/40 hover:border-border/70'
                        }`}
                        onClick={() => !uploading && setSelectedTrack(t.slug)}
                        data-testid={`music-track-${t.slug}`}
                      >
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); togglePreview(t.slug); }}
                          disabled={uploading}
                          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors mt-0.5 ${
                            isPlaying ? 'bg-primary text-black' : 'bg-muted hover:bg-muted/80 text-primary'
                          }`}
                          aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                          data-testid={`music-preview-${t.slug}`}
                        >
                          {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold truncate">{t.title}</p>
                            {t.is_default && (
                              <span className="text-[9px] uppercase tracking-widest bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                                Favorite
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{t.vibe}</p>
                          {t.moods?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {t.moods.map((m) => (
                                <span
                                  key={m}
                                  className="text-[10px] uppercase tracking-wider bg-muted/60 text-muted-foreground px-1.5 py-0.5 rounded"
                                >
                                  {m}
                                </span>
                              ))}
                            </div>
                          )}
                          {t.best_for && (
                            <p className="text-[10px] text-muted-foreground/70 mt-1.5 leading-snug">
                              Perfect for: {t.best_for}
                            </p>
                          )}
                          <p className="text-[10px] text-muted-foreground/50 mt-1">
                            {t.author} · {t.license}
                          </p>
                        </div>
                        {isSelected && (
                          <CheckCircle size={16} className="text-primary shrink-0 mt-1" />
                        )}
                      </div>
                    );
                  })}
                  <p className="text-[10px] text-muted-foreground/70 leading-relaxed pt-1">
                    Your original audio is preserved at 20% under the music — voices and ambient sound stay audible.
                  </p>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider font-bold h-12"
            data-testid="submit-attempt-btn"
          >
            {uploading || submitting ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> {buttonLabel}</>
            ) : (
              <><Sparkles size={16} className="mr-2" /> {buttonLabel}</>
            )}
          </Button>

          {/* Progress UI — visible during upload + server-side branding */}
          {uploading && (
            <div className="space-y-3 pt-1" data-testid="upload-progress-block">
              {phase === 'uploading' && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                      <Upload size={12} /> Uploading video
                    </span>
                    <span className="text-primary font-bold tabular-nums" data-testid="upload-progress-pct">
                      {progress}%
                    </span>
                  </div>
                  <Progress value={progress} className="h-2" data-testid="upload-progress-bar" />
                </>
              )}

              {phase === 'branding' && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="uppercase tracking-widest text-primary flex items-center gap-2">
                      <Sparkles size={12} /> Adding Meybra overlay
                    </span>
                    <span className="text-primary font-bold tabular-nums" data-testid="branding-progress-pct">
                      {brandingProgress}%
                    </span>
                  </div>
                  <Progress value={brandingProgress} className="h-2" data-testid="branding-progress-bar" />
                  <p className="text-[11px] text-muted-foreground leading-relaxed text-center">
                    Generating your branded video with title, score, and Meybra signature.
                    This usually takes <span className="text-foreground font-semibold">30–60 seconds</span> — please keep this screen open.
                  </p>
                </>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center">
            By submitting, you confirm the score matches your video. Meybra adds branded overlays automatically — your video is ready to share.
          </p>
        </div>
      )}
    </div>
  );
};

export default ChallengeUpload;
